import { useEffect, useState, useCallback } from "react"
import { getAuditLog, getActivityIcon, timeAgo } from "@/services/audit.service"
import { useToast } from "@/context/ToastContext"
import type { AuditLog } from "@/types"
import { Badge } from "@/components/common/Badge"
import { Button } from "@/components/common/Button"
import { EmptyState } from "@/components/common/EmptyState"
import { LoadingRow } from "@/components/common/Spinner"
import { RefreshCw, Download } from "lucide-react"

const ACTION_FILTERS = [
  { value: "all",                  label: "All Actions" },
  { value: "email_change",         label: "Email Changes" },
  { value: "password_reset",       label: "Password Resets" },
  { value: "user_suspended",       label: "Suspensions" },
  { value: "ownership_transfer",   label: "Transfers" },
  { value: "commission_adjustment",label: "Adjustments" },
  { value: "payout_approved",      label: "Payouts" },
  { value: "announcement_sent",    label: "Announcements" },
]

export function AuditLogPage() {
  const { toast } = useToast()
  const [logs, setLogs]     = useState<AuditLog[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter]   = useState("all")

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getAuditLog(100, filter === "all" ? undefined : filter)
      setLogs(data)
    } catch { toast("Failed to load audit log", "error") }
    finally { setLoading(false) }
  }, [filter, toast])

  useEffect(() => { load() }, [load])

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <select
          value={filter}
          onChange={e => setFilter(e.target.value)}
          className="px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-zimrah-500 transition-all"
        >
          {ACTION_FILTERS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
        <Button variant="secondary" icon={<RefreshCw size={14} />} onClick={load}>Refresh</Button>
        <Button variant="secondary" icon={<Download size={14} />} onClick={() => toast("Export coming in Phase 4", "info")}>Export</Button>
        <Badge variant="muted" >{logs.length} entries</Badge>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                {["Time", "Admin", "Action", "Target", "Details"].map(h => (
                  <th key={h} className="px-6 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-gray-400">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? <LoadingRow cols={5} /> :
               logs.length === 0 ? (
                <tr><td colSpan={5}>
                  <EmptyState icon="📋" title="No audit log entries" subtitle="Admin actions will appear here." />
                </td></tr>
               ) : logs.map(l => (
                <tr key={l.id} className="border-t border-gray-50 hover:bg-gray-50/50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <p className="text-sm font-semibold text-gray-700">{new Date(l.created_at).toLocaleString()}</p>
                    <p className="text-xs text-gray-400">{timeAgo(l.created_at)}</p>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{l.admin_email}</td>
                  <td className="px-6 py-4">
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-700">
                      <span>{getActivityIcon(l.action_type)}</span>
                      <span className="capitalize">{l.action_type.replace(/_/g, " ")}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {l.target_email ?? l.target_user_id?.slice(0, 8) + "…" ?? "—"}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                    {l.description ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}